/**
 * Wave 8 — AI Pursuit Brief: Approval Brief on primary lineage entity + deterministic fallback.
 */
import React from 'react';
import { Alert, Card, List, Space, Tag, Typography } from 'antd';
import { AiApprovalBriefPanel } from '@/components/approval/AiApprovalBriefPanel';
import type { PursuitWorkspaceViewModel } from './pursuitWorkspaceModel';
import { resolvePursuitBriefTarget } from './pursuitBriefTarget';
import { buildEarlyPursuitBriefSignals } from './earlyPursuitBrief';

const { Text } = Typography;

const kindColor: Record<string, string> = {
  FACT: 'blue',
  CALCULATION: 'green',
  MISSING: 'orange',
  INFERENCE: 'purple',
};

export type EpcPursuitBriefPanelProps = {
  vm: PursuitWorkspaceViewModel;
};

export const EpcPursuitBriefPanel: React.FC<EpcPursuitBriefPanelProps> = ({ vm }) => {
  const target = resolvePursuitBriefTarget(vm.lineage);
  const earlySignals = buildEarlyPursuitBriefSignals(vm);

  const deterministic = (
    <Card size="small" title="Pursuit brief (deterministic)" style={{ marginBottom: 12 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Text>
          <Text type="secondary">Stage:</Text> {vm.commercialSummary.stageLabel}
        </Text>
        <Text>
          <Text type="secondary">Client:</Text> {vm.commercialSummary.client}
        </Text>
        <Text>
          <Text type="secondary">Owner:</Text> {vm.commercialSummary.owner}
        </Text>
        <Text strong>Early pursuit signals</Text>
        <List
          size="small"
          dataSource={earlySignals.slice(0, 8)}
          renderItem={(s) => (
            <List.Item>
              <Tag color={kindColor[s.kind] || 'default'}>{s.kind}</Tag>
              <Text>{s.label}</Text>
              {s.detail && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {s.detail}
                </Text>
              )}
            </List.Item>
          )}
        />
        {vm.attention.length ? (
          <>
            <Text strong>Attention</Text>
            <List
              size="small"
              dataSource={vm.attention.slice(0, 5)}
              renderItem={(a) => (
                <List.Item>
                  <Tag color={a.severity === 'critical' ? 'error' : a.severity === 'warning' ? 'warning' : 'default'}>
                    {a.severity}
                  </Tag>
                  <Text>{a.title}</Text>
                </List.Item>
              )}
            />
          </>
        ) : (
          <Alert type="success" showIcon message="No deterministic attention items" />
        )}
        {!target ? (
          <Alert
            type="info"
            showIcon
            message="No approval-backed entity in lineage yet"
            description="AI Approval Brief appears when bid / submission / award / mobilization is linked."
          />
        ) : null}
      </Space>
    </Card>
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {deterministic}
      {target ? (
        <div>
          <Space wrap style={{ marginBottom: 8 }}>
            <Text strong>AI Approval Brief</Text>
            <Tag>{target.label}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {target.entityType}
            </Text>
          </Space>
          <AiApprovalBriefPanel entityType={target.entityType} entityId={target.entityId} />
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message="Brief is advisory — decisions still go through Approval Runtime"
          />
        </div>
      ) : null}
    </Space>
  );
};

export default EpcPursuitBriefPanel;
